import { Theme as ThemeContext } from '@lib/emotion'
import Color from 'color'
import { ThemeProvider as Provider } from 'emotion-theming'
import { GlobalStyles } from './elements'
import GET_SETTINGS from './Settings.graphql'

import { Settings, Settings_settings_theme } from '@generated'
import * as Constants from '@constants'
import { useQuery } from 'react-apollo-hooks'
import {useRouter} from '@hooks'
import {generalStore, authStore} from '@store';
import { useEffect, useRef, useCallback } from 'react'
import { decode } from 'jsonwebtoken';

const queryParams = new URLSearchParams(location.search)

export const ThemeProvider = ({ children }) => {
  const guild = useRouter()?.guild ?? '299881420891881473'
  const { data: {settings} } = useQuery<Settings>(GET_SETTINGS, { variables: { guild }, fetchPolicy: 'network-only' })
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);

  // Render Turnstile widget as soon as container is available (starts verification early)
  const setupTurnstile = useCallback((container: HTMLDivElement | null) => {
    if (!container || turnstileContainerRef.current === container) return;
    turnstileContainerRef.current = container;

    if (!settings?.captchaSiteKey || !window.turnstile) return;

    turnstileWidgetId.current = window.turnstile.render(container, {
      sitekey: settings.captchaSiteKey,
      theme: 'dark',
      size: 'invisible',
    });
  }, [settings?.captchaSiteKey]);

  let theme: Settings_settings_theme = {
    __typename: 'ThemeSettings',
    colors: {
      __typename: 'ThemeColorSettings',
      primary: settings?.theme?.colors?.primary || Constants.THEME_COLOR_PRIMARY,
      accent: settings?.theme?.colors?.accent || Constants.THEME_COLOR_ACCENT,
      background: settings?.theme?.colors?.background || Constants.THEME_BACKGROUND
    },
    css: settings?.theme?.css || ``
  };

  generalStore.setSettings(settings)

  generalStore.setAccessibility(queryParams.get('accessibility'))

  useEffect(() => {
    if (queryParams.has('token')) {
      const token = decodeURIComponent(queryParams.get('token'))

      let ls: Storage
      try {
        ls = localStorage
      } catch (e) {
        generalStore.toggleMenu(true)
      }

      if (ls) {
        let decodedToken;

        try {
          decodedToken = decode(token);
        } catch (e) {
        }

        if (decodedToken?.issuer === 'WidgetBot Backend') {
          authStore.setToken(token);
        } else {
          authStore.guildLogin(guild, token).then(async () => {
            generalStore.needsUpdate = true;
          })
        }
      }
    } else if (queryParams.has('username') && settings) {
      const name = decodeURIComponent(queryParams.get('username'))
      if (name !== authStore.user?.username) {
        let ls: Storage
        try {
          ls = localStorage
        } catch (e) {
          generalStore.toggleMenu(true)
        }

        if (ls) {
          const captchaEnabled = settings?.isCaptchaEnabled && settings?.captchaSiteKey;

          if (captchaEnabled) {
            // Poll for captcha response (widget already rendered via ref callback)
            const waitForResponse = () => {
              if (window.turnstile && turnstileWidgetId.current) {
                const captchaResponse = window.turnstile.getResponse(turnstileWidgetId.current);
                if (captchaResponse) {
                  authStore.guestLogin(name, captchaResponse).then(async () => {
                    generalStore.needsUpdate = true;
                  });
                  return;
                }
              }
              setTimeout(waitForResponse, 50);
            };
            waitForResponse();
          } else {
            // Captcha not enabled, proceed without token
            authStore.guestLogin(name).then(async () => {
              generalStore.needsUpdate = true;
            });
          }
        }
      }
    }
  }, [settings]);

  const themeContext: ThemeContext = {
    ...theme,
    readonly: settings?.readonly || false,
    guestMode: settings?.guestMode || false,
    singleChannel: !!settings?.singleChannel || settings?.hideSidebar || false,
    colors: {
      ...theme.colors,
      _primary: Color(theme.colors.primary),
      _background: Color(theme.colors.background),
      _accent: Color(theme.colors.accent)
    },
    url: {
      preset: queryParams.get('preset') as 'crate' | null,
      api: queryParams.get('api')
    },
    loadedSettings: !!settings
  };

  GlobalStyles.inject(themeContext);

  return (
    <Provider theme={themeContext}>
      <>
        {settings?.isCaptchaEnabled && settings?.captchaSiteKey && (
          <div ref={setupTurnstile} style={{ display: 'none' }} />
        )}
        {children}
      </>
    </Provider>
  )
};



// WEBPACK FOOTER //
// ./src/app/ThemeProvider.tsx